import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { productSchema, breadcrumbSchema } from "@/components/SEO";
import { API } from "@/App";
import { useCart } from "@/contexts/CartContext";
import {
  ShoppingCart,
  FileText,
  BookOpen,
  GraduationCap,
  Star,
  Download,
  Eye,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  LogIn,
  Filter,
  Search,
  CheckCircle,
  Loader2,
  IndianRupee,
  Lightbulb,
  Target,
  Award,
  Plus,
  Check,
  ShoppingBag,
} from "lucide-react";

export default function LearningShop() {
  const navigate = useNavigate();
  const { addToCart, isInCart, setIsCartOpen, getCartCount } = useCart();
  const [catalog, setCatalog] = useState([]);
  const [productTypes, setProductTypes] = useState({});
  const [supportedGrades, setSupportedGrades] = useState([]);
  const [supportedSubjects, setSupportedSubjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState(5); // Default to Class 5
  const [selectedSubject, setSelectedSubject] = useState("mathematics"); // Default to Math
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewQuestions, setPreviewQuestions] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Handle add to cart
  const handleAddToCart = (product) => {
    const added = addToCart(product);
    if (added) {
      toast.success(`Added to cart: ${product.topic_display}`);
    } else {
      toast.info("Already in cart");
    }
  };

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem("parent_token") || localStorage.getItem("token");
    setIsAuthenticated(!!token);
  }, []);

  // Fetch catalog
  useEffect(() => {
    fetchCatalog();
  }, [selectedGrade, selectedSubject]);

  const fetchCatalog = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/shop/catalog`;
      const params = new URLSearchParams();
      if (selectedGrade) params.append("grade", selectedGrade);
      if (selectedSubject) params.append("subject", selectedSubject);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setCatalog(data.catalog || []);
        setProductTypes(data.product_types || {});
        setSupportedGrades(data.supported_grades || []);
        setSupportedSubjects(data.supported_subjects || []);
      }
    } catch (error) {
      console.error("Error fetching catalog:", error);
      toast.error("Failed to load shop catalog");
    }
    setIsLoading(false);
  };

  const handlePreview = async (product) => {
    setPreviewProduct(product);
    setPreviewQuestions([]);
    setIsLoadingPreview(true);

    try {
      const response = await fetch(`${API}/shop/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ product_id: product.id }),
      });
      const data = await response.json();
      
      if (data.success) {
        setPreviewQuestions(data.preview_questions || []);
      } else {
        toast.error("Failed to generate preview");
      }
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Failed to generate preview");
    }
    setIsLoadingPreview(false);
  };

  const handlePurchase = async (product) => {
    if (!isAuthenticated) {
      toast.info("Please login to purchase");
      navigate("/login?redirect=/shop");
      return;
    }

    // For now, show coming soon as Razorpay is not integrated yet
    toast.info("Payment gateway coming soon! Stay tuned.");
  };

  const closePreview = () => {
    setPreviewProduct(null);
    setPreviewQuestions([]);
  };

  const filteredCatalog = catalog.filter((category) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchingProducts = category.products.filter(
        (p) =>
          p.topic_display.toLowerCase().includes(query) ||
          p.subject_display.toLowerCase().includes(query)
      );
      return matchingProducts.length > 0;
    }
    return true;
  });

  const getFilteredProducts = (category) => {
    if (!searchQuery) return category.products;
    const query = searchQuery.toLowerCase();
    return category.products.filter(
      (p) =>
        p.topic_display.toLowerCase().includes(query) ||
        p.subject_display.toLowerCase().includes(query)
    );
  };

  // Group products by topic for cleaner display
  const groupProductsByTopic = (products) => {
    const grouped = {};
    products.forEach((product) => {
      if (!grouped[product.topic]) {
        grouped[product.topic] = {
          topic: product.topic,
          topic_display: product.topic_display,
          worksheet: null,
          question_bank: null,
        };
      }
      if (product.product_type === "worksheet") {
        grouped[product.topic].worksheet = product;
      } else {
        grouped[product.topic].question_bank = product;
      }
    });
    return Object.values(grouped);
  };

  const subjectColors = {
    mathematics: "from-blue-500 to-indigo-600",
    science: "from-green-500 to-emerald-600",
    english: "from-purple-500 to-violet-600",
    hindi: "from-orange-500 to-amber-600",
  };

  const subjectIcons = {
    mathematics: "🔢",
    science: "🔬",
    english: "📖",
    hindi: "🇮🇳",
  };

  const fmtMix = (m) => (m ? `${m.easy}E · ${m.medium}M · ${m.hard}H` : "");

  // SEO structured data for the shop
  const shopStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Store",
        "name": "EduQuest - Book-Grounded Question Bank Store",
        "description": "Book-grounded worksheets and question banks for grades 5-8, drawn from our CBSE-aligned Question Bank. Practice materials for Math, Science, English, and Hindi.",
        "url": "https://eduzy.co/eduquest"
      },
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "EduQuest", url: "/eduquest" }
      ])
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <SEO
        title="EduQuest - Book-Grounded Question Banks & Worksheets | Grades 5-8 | Eduzy"
        description="Download book-grounded worksheets (₹25 · 25 questions) and question banks (₹50 · 50 questions) for grades 5-8. CBSE-aligned Math, Science, English, Hindi with instant PDF downloads. Guest checkout available."
        keywords="question bank, worksheets, CBSE practice papers, math worksheets, science questions, grades 5-8, PDF download, practice tests, exam preparation"
        canonicalUrl="/eduquest"
        structuredData={shopStructuredData}
      />
      
      {/* Floating Cart Button */}
      {getCartCount() > 0 && (
        <button
          onClick={() => setIsCartOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-amber-600 hover:bg-amber-700 text-white rounded-full p-4 shadow-lg shadow-amber-600/30 transition-all hover:scale-105"
          data-testid="floating-cart-btn"
        >
          <ShoppingCart className="w-6 h-6" />
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
            {getCartCount()}
          </span>
        </button>
      )}

      {/* Header */}
      <SharedHeader
        rightContent={
          <div className="flex items-center gap-3">
            {/* Cart Button */}
            <Button
              onClick={() => setIsCartOpen(true)}
              variant="outline"
              className="relative border-amber-600 text-amber-600 hover:bg-amber-50"
              data-testid="header-cart-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Cart
              {getCartCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {getCartCount()}
                </span>
              )}
            </Button>
            
            {isAuthenticated ? (
              <>
                <Button
                  onClick={() => navigate("/purchases")}
                  variant="ghost"
                  className="text-emerald-600 hover:bg-emerald-50"
                  data-testid="shop-purchases-btn"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  My Purchases
                </Button>
                <Button
                  onClick={() => navigate("/parent/dashboard")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="shop-dashboard-btn"
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <Button
                onClick={() => navigate("/login?redirect=/shop")}
                variant="outline"
                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                data-testid="shop-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Login
              </Button>
            )}
          </div>
        }
      />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 bg-gradient-to-br from-amber-500 via-orange-500 to-orange-600 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-yellow-300/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 -left-10 w-48 h-48 bg-red-400/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-yellow-200" />
            <Badge className="bg-yellow-400 text-yellow-900 border-yellow-500 text-sm font-bold px-3 py-1">
              Book-Grounded Content
            </Badge>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            EduQuest Learning Shop
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-6">
            Premium worksheets and question banks for Classes 5-8.
            Drawn from our CBSE-aligned, book-grounded Question Bank, ready to print!
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
            <div className="flex items-center gap-2 bg-amber-600 text-white rounded-full px-5 py-2.5 shadow-lg shadow-amber-600/30">
              <FileText className="w-5 h-5" />
              <span className="font-semibold">Worksheets ₹25 · 25 Qs</span>
            </div>
            <div className="flex items-center gap-2 bg-orange-600 text-white rounded-full px-5 py-2.5 shadow-lg shadow-orange-600/30">
              <BookOpen className="w-5 h-5" />
              <span className="font-semibold">Question Banks ₹50 · 50 Qs</span>
            </div>
            <div className="flex items-center gap-2 bg-yellow-600 text-white rounded-full px-5 py-2.5 shadow-lg shadow-yellow-600/30">
              <Download className="w-5 h-5" />
              <span className="font-semibold">Instant PDF Download</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Option 2: Class Tabs + Subject Pills */}
      <main className="flex-1 py-6">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
              <span className="ml-3 text-gray-600">Loading catalog...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Class Tabs */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-1.5">
                <div className="flex flex-wrap justify-center gap-1">
                  {supportedGrades.map((grade) => {
                    const isActive = selectedGrade === grade;
                    return (
                      <button
                        key={grade}
                        onClick={() => setSelectedGrade(grade)}
                        className={`px-5 sm:px-8 py-2.5 rounded-xl font-bold text-sm transition-all ${
                          isActive
                            ? "bg-amber-600 text-white shadow-md"
                            : "text-gray-600 hover:bg-gray-100"
                        }`}
                        data-testid={`class-tab-${grade}`}
                      >
                        Class {grade}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Subject Pills */}
              <div className="flex justify-center gap-2 flex-wrap">
                <span className="text-sm text-gray-500 self-center mr-1">Subject:</span>
                {supportedSubjects.map((subject) => {
                  const isActive = selectedSubject === subject;
                  const icon = subjectIcons[subject] || "📚";
                  const colorConfig = {
                    mathematics: { active: "bg-blue-600", hover: "hover:bg-blue-100 hover:text-blue-700" },
                    science: { active: "bg-green-600", hover: "hover:bg-green-100 hover:text-green-700" },
                    english: { active: "bg-purple-600", hover: "hover:bg-purple-100 hover:text-purple-700" },
                    hindi: { active: "bg-orange-600", hover: "hover:bg-orange-100 hover:text-orange-700" },
                  }[subject] || { active: "bg-gray-600", hover: "hover:bg-gray-100" };
                  
                  return (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm transition-all ${
                        isActive
                          ? `${colorConfig.active} text-white shadow-md`
                          : `bg-gray-100 text-gray-600 ${colorConfig.hover}`
                      }`}
                      data-testid={`subject-pill-${subject}`}
                    >
                      <span className="text-base">{icon}</span>
                      <span className="hidden sm:inline">{subject.charAt(0).toUpperCase() + subject.slice(1)}</span>
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="flex justify-center">
                <div className="relative w-full max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search topics..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 rounded-full border-gray-200"
                    data-testid="shop-search-input"
                  />
                </div>
              </div>

              {/* Products Table */}
              {(() => {
                const currentCategory = catalog.find(
                  (c) => c.subject === selectedSubject && c.grade === selectedGrade
                );
                
                if (!currentCategory) {
                  return (
                    <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                      <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No products found for this selection</p>
                    </div>
                  );
                }

                const products = searchQuery
                  ? currentCategory.products.filter((p) =>
                      p.topic_display.toLowerCase().includes(searchQuery.toLowerCase())
                    )
                  : currentCategory.products;

                const topicGroups = groupProductsByTopic(products);
                const icon = subjectIcons[selectedSubject] || "📚";

                if (topicGroups.length === 0) {
                  return (
                    <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                      <Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No topics match your search</p>
                    </div>
                  );
                }

                return (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-4 py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{icon}</span>
                          <h3 className="font-bold text-gray-800">
                            {selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)} - Class {selectedGrade}
                          </h3>
                        </div>
                        <span className="text-sm text-gray-500">{topicGroups.length} topics</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                        Every pack is 40% easy · 40% medium · 20% hard, drawn from our CBSE-aligned, book-grounded bank.
                      </p>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 bg-gray-50/50">
                            <th className="text-left py-3 px-4 font-semibold text-gray-600 text-sm">Topic</th>
                            <th className="text-center py-3 px-2 sm:px-4 font-semibold text-blue-600 text-sm">
                              <div className="flex items-center justify-center gap-1">
                                <FileText className="w-4 h-4 hidden sm:block" />
                                <span>Worksheet</span>
                                <span className="text-xs bg-blue-100 px-1.5 py-0.5 rounded hidden sm:inline">₹25 · 25 Qs</span>
                              </div>
                            </th>
                            <th className="text-center py-3 px-2 sm:px-4 font-semibold text-purple-600 text-sm">
                              <div className="flex items-center justify-center gap-1">
                                <BookOpen className="w-4 h-4 hidden sm:block" />
                                <span>Q-Bank</span>
                                <span className="text-xs bg-purple-100 px-1.5 py-0.5 rounded hidden sm:inline">₹50 · 50 Qs</span>
                              </div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {topicGroups.map((topicGroup, idx) => (
                            <tr 
                              key={topicGroup.topic} 
                              className={`border-b border-gray-100 hover:bg-emerald-50/50 transition-colors ${
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50/30"
                              }`}
                            >
                              <td className="py-3 px-4">
                                <span className="font-medium text-gray-800 text-sm sm:text-base">{topicGroup.topic_display}</span>
                              </td>
                              <td className="py-2 px-2 sm:px-4 text-center">
                                {topicGroup.worksheet && (
                                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                                    <span className="text-xs text-gray-400 hidden lg:inline" title="Difficulty mix: easy · medium · hard">{fmtMix(topicGroup.worksheet.difficulty_mix)}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handlePreview(topicGroup.worksheet)}
                                      className="text-gray-400 hover:text-blue-600 p-1 h-7 w-7"
                                      title="Preview"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </Button>
                                    {isInCart(topicGroup.worksheet.id) ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-green-500 text-green-600 h-7 px-2 text-xs"
                                        disabled
                                      >
                                        <Check className="w-3 h-3 sm:mr-1" />
                                        <span className="hidden sm:inline">In Cart</span>
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => handleAddToCart(topicGroup.worksheet)}
                                        className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2 text-xs"
                                        title="Add to Cart"
                                      >
                                        <Plus className="w-3 h-3 sm:mr-1" />
                                        <span className="hidden sm:inline">Add</span>
                                        <span className="sm:hidden">₹25</span>
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-2 sm:px-4 text-center">
                                {topicGroup.question_bank && (
                                  <div className="flex items-center justify-center gap-1 sm:gap-2">
                                    <span className="text-xs text-gray-400 hidden lg:inline" title="Difficulty mix: easy · medium · hard">{fmtMix(topicGroup.question_bank.difficulty_mix)}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handlePreview(topicGroup.question_bank)}
                                      className="text-gray-400 hover:text-purple-600 p-1 h-7 w-7"
                                      title="Preview"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </Button>
                                    {isInCart(topicGroup.question_bank.id) ? (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-green-500 text-green-600 h-7 px-2 text-xs"
                                        disabled
                                      >
                                        <Check className="w-3 h-3 sm:mr-1" />
                                        <span className="hidden sm:inline">In Cart</span>
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => handleAddToCart(topicGroup.question_bank)}
                                        className="bg-purple-600 hover:bg-purple-700 text-white h-7 px-2 text-xs"
                                        title="Add to Cart"
                                      >
                                        <Plus className="w-3 h-3 sm:mr-1" />
                                        <span className="hidden sm:inline">Add</span>
                                        <span className="sm:hidden">₹50</span>
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </main>

      {/* Preview Modal */}
      {previewProduct && (
        <PreviewModal
          product={previewProduct}
          questions={previewQuestions}
          isLoading={isLoadingPreview}
          onClose={closePreview}
          onPurchase={() => handlePurchase(previewProduct)}
          isAuthenticated={isAuthenticated}
        />
      )}

      {/* Value Props Section */}
      <section className="py-12 bg-white border-t">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
            Why Buy from Eduzy Learning Shop?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-emerald-100 rounded-full flex items-center justify-center">
                <Target className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">CBSE Aligned</h3>
              <p className="text-gray-600 text-sm">
                All content follows NCERT curriculum standards for Classes 5-8
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Lightbulb className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Book-Grounded Quality</h3>
              <p className="text-gray-600 text-sm">
                Curated from our teacher-approved Question Bank, grounded in the textbook
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 bg-purple-100 rounded-full flex items-center justify-center">
                <Award className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Instant Download</h3>
              <p className="text-gray-600 text-sm">
                Get print-ready PDFs with answer keys immediately
              </p>
            </div>
          </div>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

// Simple Product Card for Option B (Tab View) - Minimal, compact design
function SimpleProductCard({ product, onPreview, onPurchase }) {
  const isWorksheet = product.product_type === "worksheet";
  
  return (
    <div 
      className={`bg-white rounded-xl border p-3 hover:shadow-md transition-all cursor-pointer group ${
        isWorksheet ? "border-blue-100 hover:border-blue-300" : "border-purple-100 hover:border-purple-300"
      }`}
      data-testid={`simple-card-${product.id}`}
    >
      <div className="text-center">
        <h4 className="font-medium text-gray-800 text-sm mb-2 line-clamp-2 min-h-[2.5rem]">
          {product.topic_display}
        </h4>
        <p className="text-xs text-gray-500 mb-3">
          {product.question_count} Qs
        </p>
        <div className="flex items-center justify-center gap-1 mb-3">
          <span className={`font-bold text-lg ${isWorksheet ? "text-blue-600" : "text-purple-600"}`}>
            ₹{product.price}
          </span>
        </div>
        <div className="flex gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); onPreview(); }}
            className="flex-1 text-gray-500 hover:text-emerald-600 px-2 h-8"
          >
            <Eye className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm"
            onClick={(e) => { e.stopPropagation(); onPurchase(); }}
            className={`flex-1 text-white px-2 h-8 ${
              isWorksheet ? "bg-blue-600 hover:bg-blue-700" : "bg-purple-600 hover:bg-purple-700"
            }`}
          >
            Buy
          </Button>
        </div>
      </div>
    </div>
  );
}

// Topic Row Component - Option A: Topic-First Layout
function TopicRow({ topicGroup, onPreview, onPurchase }) {
  return (
    <div 
      className="bg-gray-50 rounded-xl border border-gray-200 p-4 hover:border-emerald-300 transition-colors"
      data-testid={`topic-row-${topicGroup.topic}`}
    >
      {/* Topic Header */}
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-200">
        <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-emerald-600" />
        </div>
        <h4 className="font-semibold text-gray-800 text-lg">{topicGroup.topic_display}</h4>
      </div>

      {/* Product Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Worksheet Option */}
        {topicGroup.worksheet && (
          <div className="flex items-center justify-between bg-white rounded-lg border border-blue-100 p-3 hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-sm">Worksheet</p>
                <p className="text-xs text-gray-500">{topicGroup.worksheet.question_count} questions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-emerald-600 flex items-center">
                <IndianRupee className="w-3 h-3" />{topicGroup.worksheet.price}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onPreview(topicGroup.worksheet)}
                className="text-gray-500 hover:text-emerald-600 px-2"
                data-testid={`preview-ws-${topicGroup.topic}`}
              >
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => onPurchase(topicGroup.worksheet)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-3"
                data-testid={`buy-ws-${topicGroup.topic}`}
              >
                Buy
              </Button>
            </div>
          </div>
        )}

        {/* Question Bank Option */}
        {topicGroup.question_bank && (
          <div className="flex items-center justify-between bg-white rounded-lg border border-purple-100 p-3 hover:shadow-sm transition-shadow">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-sm">Question Bank</p>
                <p className="text-xs text-gray-500">{topicGroup.question_bank.question_count} questions</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-emerald-600 flex items-center">
                <IndianRupee className="w-3 h-3" />{topicGroup.question_bank.price}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onPreview(topicGroup.question_bank)}
                className="text-gray-500 hover:text-emerald-600 px-2"
                data-testid={`preview-qb-${topicGroup.topic}`}
              >
                <Eye className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => onPurchase(topicGroup.question_bank)}
                className="bg-purple-600 hover:bg-purple-700 text-white px-3"
                data-testid={`buy-qb-${topicGroup.topic}`}
              >
                Buy
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Product Card Component (kept for reference/fallback)
function ProductCard({ product, onPreview, onPurchase }) {
  const isWorksheet = product.product_type === "worksheet";
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-all duration-300 hover:border-emerald-300"
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <Badge variant="outline" className={`text-xs ${isWorksheet ? 'border-blue-300 text-blue-600' : 'border-purple-300 text-purple-600'}`}>
            {product.icon} {product.product_name}
          </Badge>
        </div>
        <div className="text-right">
          <div className="flex items-center text-emerald-600 font-bold text-lg">
            <IndianRupee className="w-4 h-4" />
            {product.price}
          </div>
        </div>
      </div>
      
      <h4 className="font-semibold text-gray-800 mb-1">
        {product.topic_display}
      </h4>
      <p className="text-sm text-gray-500 mb-3">
        {product.question_count} questions with solutions
      </p>
      
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPreview}
          className="flex-1 text-emerald-600 border-emerald-300 hover:bg-emerald-50"
          data-testid={`preview-btn-${product.id}`}
        >
          <Eye className="w-4 h-4 mr-1" />
          Preview
        </Button>
        <Button
          size="sm"
          onClick={onPurchase}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid={`buy-btn-${product.id}`}
        >
          <ShoppingCart className="w-4 h-4 mr-1" />
          Buy
        </Button>
      </div>
    </div>
  );
}

// Preview Modal Component
function PreviewModal({ product, questions, isLoading, onClose, onPurchase, isAuthenticated }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-2">
                {product.icon} {product.product_name}
              </Badge>
              <h2 className="text-xl font-bold">{product.topic_display}</h2>
              <p className="text-sm text-white/80 mt-1">
                Class {product.grade} • {product.subject_display}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white text-2xl font-bold"
              data-testid="close-preview-btn"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h3 className="font-semibold text-gray-800">Sample Questions Preview</h3>
            <Badge variant="outline" className="text-xs">3 of {product.question_count}</Badge>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
              <span className="ml-3 text-gray-600">Generating preview...</span>
            </div>
          ) : questions.length > 0 ? (
            <div className="space-y-4">
              {questions.map((q, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-7 h-7 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-gray-800 font-medium mb-2">{q.question}</p>
                      
                      {q.type === "mcq" && q.options && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {q.options.map((opt, i) => (
                            <div key={i} className="text-sm text-gray-600 bg-white rounded px-3 py-1.5 border">
                              <span className="font-medium text-gray-400 mr-2">{String.fromCharCode(65 + i)}.</span>
                              {opt}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {q.type === "true_false" && (
                        <div className="flex gap-3 mt-2">
                          <span className="text-sm text-gray-600 bg-white rounded px-3 py-1.5 border">True</span>
                          <span className="text-sm text-gray-600 bg-white rounded px-3 py-1.5 border">False</span>
                        </div>
                      )}
                      
                      {q.type === "fill_blank" && (
                        <div className="mt-2 text-sm text-gray-500 italic">
                          (Fill in the blank)
                        </div>
                      )}
                      
                      {q.hint && (
                        <div className="mt-3 flex items-start gap-2 text-sm text-amber-600 bg-amber-50 rounded px-3 py-2">
                          <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          <span>Hint: {q.hint}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Unable to load preview. Please try again.
            </div>
          )}

          <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Full version includes:</span>
            </div>
            <ul className="mt-2 text-sm text-emerald-600 space-y-1 ml-7">
              <li>• {product.question_count} carefully crafted questions</li>
              <li>• Complete answer key with explanations</li>
              <li>• Print-ready PDF format</li>
              <li>• Space for student working</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 border-t flex items-center justify-between">
          <div className="flex items-center text-2xl font-bold text-emerald-600">
            <IndianRupee className="w-6 h-6" />
            {product.price}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button 
              onClick={onPurchase}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="purchase-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {isAuthenticated ? "Buy Now" : "Login to Purchase"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
